import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useNavigate } from 'react-router-dom';
import { login, clearError } from '../redux/slices/authSlice';
import { AppDispatch, RootState } from '../redux/store';
import {
  Box,
  Card,
  CardContent,
  TextField,
  Button,
  Typography,
  Alert,
  InputAdornment,
  IconButton,
  Container,
  Avatar,
  CircularProgress,
  Fade,
  Slide,
} from '@mui/material';
import {
  Visibility,
  VisibilityOff,
  Login as LoginIcon,
  Business as BusinessIcon,
} from '@mui/icons-material';

const Login: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const { loading, error, isAuthenticated, user } = useSelector((state: RootState) => state.auth);

  const [formData, setFormData] = useState({
    email: '',
    password: '',
  });
  const [showPassword, setShowPassword] = useState(false);

  useEffect(() => {
    // Nettoyer les erreurs quand le composant se démonte
    return () => {
      dispatch(clearError());
    };
  }, [dispatch]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    console.log('Submitting login with:', formData);
    const result = await dispatch(login(formData));

    console.log('Login result:', result);
    console.log('Result type:', result.type);
    console.log('Result payload:', result.payload);

    // Si le login réussit, rediriger manuellement
    if (result.type === 'auth/login/fulfilled' && result.payload) {
      const payload = result.payload as { user: { role: string }; token: string };
      const userRole = payload.user.role;
      console.log('Login successful! User role:', userRole);
      console.log('Navigating to dashboard...');

      // Utiliser setTimeout pour forcer la redirection après mise à jour du state
      setTimeout(() => {
        if (userRole === 'cyber') {
          console.log('Redirecting to /cyber-dashboard');
          window.location.href = '/cyber-dashboard';
        } else {
          console.log('Redirecting to /admin');
          window.location.href = '/admin';
        }
      }, 100);
    } else {
      console.log('Login failed or pending');
      console.log('Result meta:', result.meta);
    }
  };

  const handleTogglePassword = () => {
    setShowPassword(!showPassword);
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'linear-gradient(135deg, #1976d2 0%, #42a5f5 100%)',
        py: 4,
      }}
    >
      <Container maxWidth="sm">
        <Slide direction="down" in={true} timeout={500}>
          <Card
            elevation={10}
            sx={{
              borderRadius: 4,
              overflow: 'visible',
            }}
          >
            <CardContent sx={{ p: { xs: 3, sm: 5 } }}>
              {/* Logo/Icon */}
              <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
                <Avatar
                  sx={{
                    width: 80,
                    height: 80,
                    bgcolor: 'primary.main',
                    boxShadow: 3,
                  }}
                >
                  <BusinessIcon sx={{ fontSize: 48 }} />
                </Avatar>
              </Box>

              {/* Title */}
              <Typography
                variant="h4"
                component="h1"
                align="center"
                gutterBottom
                sx={{
                  fontWeight: 700,
                  color: 'text.primary',
                  mb: 1,
                }}
              >
                Gestion Multi-Service
              </Typography>

              <Typography
                variant="body1"
                align="center"
                color="text.secondary"
                sx={{ mb: 4 }}
              >
                Connectez-vous à votre compte
              </Typography>

              {/* Error Alert */}
              {error && (
                <Fade in={true}>
                  <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>
                    {error}
                  </Alert>
                </Fade>
              )}

              {/* Form */}
              <Box component="form" onSubmit={handleSubmit} noValidate>
                <TextField
                  fullWidth
                  label="Email ou nom d'utilisateur"
                  name="email"
                  type="text"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  autoComplete="email"
                  autoFocus
                  sx={{ mb: 3 }}
                  placeholder="admin@example.com"
                />

                <TextField
                  fullWidth
                  label="Mot de passe"
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  value={formData.password}
                  onChange={handleChange}
                  required
                  autoComplete="current-password"
                  sx={{ mb: 4 }}
                  placeholder="••••••••"
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={handleTogglePassword}
                          edge="end"
                          aria-label="toggle password visibility"
                        >
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />

                <Button
                  fullWidth
                  type="submit"
                  variant="contained"
                  size="large"
                  disabled={loading}
                  startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <LoginIcon />}
                  sx={{
                    py: 1.5,
                    fontSize: '1rem',
                    fontWeight: 600,
                    textTransform: 'none',
                    boxShadow: 2,
                    '&:hover': {
                      boxShadow: 4,
                    },
                  }}
                >
                  {loading ? 'Connexion...' : 'Se connecter'}
                </Button>
              </Box>

              {/* Register Link */}
              <Box sx={{ mt: 3, textAlign: 'center' }}>
                <Typography variant="body2" color="text.secondary">
                  Pas encore de compte ?{' '}
                  <Typography
                    component={Link}
                    to="/register"
                    variant="body2"
                    sx={{
                      color: 'primary.main',
                      textDecoration: 'none',
                      fontWeight: 600,
                      '&:hover': {
                        textDecoration: 'underline',
                      },
                    }}
                  >
                    S'inscrire
                  </Typography>
                </Typography>
              </Box>

              {/* Demo Credentials */}
              <Box
                sx={{
                  mt: 4,
                  p: 2,
                  bgcolor: 'info.lighter',
                  borderRadius: 2,
                  border: '1px solid',
                  borderColor: 'info.light',
                }}
              >
                <Typography variant="caption" color="info.dark" sx={{ display: 'block', fontWeight: 600, mb: 0.5 }}>
                  Identifiants de démo:
                </Typography>
                <Typography variant="caption" color="info.dark" sx={{ display: 'block' }}>
                  Email: admin@example.com
                </Typography>
                <Typography variant="caption" color="info.dark" sx={{ display: 'block' }}>
                  Mot de passe: Admin123!
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Slide>
      </Container>
    </Box>
  );
};

export default Login;
